import { BaseLanguage } from "./baseLang.service";
import { SemanticErrorCause, SyntaxErrorCause } from "app/xlogo-parser";

export class FrenchService extends BaseLanguage {
  //#################################################################################
  //
  // This service holds hashmap containing all german strings used in our platform
  // When translating, please keep keys (first argument) same, while
  // adapting the values (seconds argument).
  //
  //#################################################################################

  constructor() {
    super();

    this.populateStaticStrings();
    this.defineLoginStrings();
    this.defineExamStrings();
    this.defineOnlineStorageStrings();
    this.defineQickStartStrings();
    this.defineRuntimeErrorStrings();
    this.defineSemanticErrorStrings();
    this.defineSyntaxErrorStrings();
    this.defineDebugWatchStrings();
    this.defineDebugControls();
    this.defineTooltips();
    this.defineProgressStrings();
    this.defineRobotStrings();
    this.defineUIstrings();
    this.defineMenuStrings();
  }

  // strings used in menu
  private defineMenuStrings() {
    this.define("menu", "Menu");
    this.define("saveFile", "Sauvegarder le fichier");
    this.define("loadFile", "Charger le fichier");
    this.define("saveCanvas", "Enregistrer l'image");
    this.define("fullscreen", "Plein écran");
    this.define("newfeatures", "Quoi de neuf?");
    this.define("Cheatsheet", "Memento des commandes");
    this.define("background", "Fonds d'écran");
    this.define("save-as-png", "Enregistrer au format png");
    this.define("save-as-svg", "Enregistrer au format svg");
    this.define("Robot", "Robots");
  }

  // user interface strings: history, header, input and general UI
  private defineUIstrings() {
    this.define("enterCommand", "Entre les commandes ici");
    this.define("cancel", "annuler");
  }

  // Robots interaction strings for BlueBot and Root
  private defineRobotStrings() {
    this.define("WhichRobotUsed", "Quel robot utilises-tu?");
    this.define("disconnectBlueBot", "BlueBot est déja connecté. Veux-tu procéder à la déconnexion?");
    this.define("RobotConnected", "Root est déjà connecté. Veux-tu procéder à la déconnexion?");
    this.define("ConnectionNotPossible", "Le connexion n'est pas possible");
    this.define("ConnectionError", "Les périphériques Bluetooth basse consommation ne sont pas supportés par votre ordinateur et/ou votre navigateur. Actualiser vos pilotes Bluetooth et tester à nouveau avec un autre appareil ou navigateur.");
    this.define("RobotDisconnect", "Déconnecter");
  }

  //Progress strings shown in header while execution
  private defineProgressStrings() {
    this.define("executingVisitor", "Exécution de commande…");
    this.define("init", "Initialisation.....");
  }

  // Strings related to tooltips in header
  private defineTooltips() {
    this.define("tooltipButtonStop", "Arrêter le programme en cours.");
    this.define("tooltipButtonDebug", "Exécuter le programme pas à pas");
    this.define("tooltipButtonForward", "Exécuter la commande précédente");
    this.define("tooltipButtonBackward", "Exécuter la commande suivante");
    this.define("tooltipButtonSpeed", "Régler la vitesse");
    this.define("tooltipButtonLanguage", "Changer la langue");
    this.define("tooltipButtonTurtle", "Changer de tortue");
  }

  // Strings related to debug controls in header
  private defineDebugControls() {
    this.define("buttonDebugToggle", "Pas à pas");
    this.define("buttonForward", "En avant");
    this.define("buttonBackward", "En arrière");
  }

  // Strings related to ebug watch
  private defineDebugWatchStrings() {
    this.define("watchPenDown", "Stylo en bas:");
    this.define("watchErasing", "Effacer:");
    this.define("watchVisible", "Visible:");
    this.define("watchColor", "Couleur du stylo:");
    this.define("watchWidth", "Largeur du stylo:");
    this.define("watchDirection", "Direction:");
  }

  // Strings related to syntactic errors
  private defineSyntaxErrorStrings() {
    this.define(SyntaxErrorCause[SyntaxErrorCause.EXTRANEOUS_INPUT], "Symbole superflu: {extraneousToken}"); 
    this.define(SyntaxErrorCause[SyntaxErrorCause.MISMATCHED_INPUT], "Symbole non valide: {mismatchedToken}");
    this.define(SyntaxErrorCause[SyntaxErrorCause.MISSING_INPUT], "Symbole {missingToken} manquant à {missingPosition}");
    this.define(SyntaxErrorCause[SyntaxErrorCause.NO_VIABLE_ALT], "Code source non valide vers {no_viable_alt_position}");
    this.define(SyntaxErrorCause[SyntaxErrorCause.UNKNOWN], "Une erreur inconnue est survenue: {msg}"); 
    this.define(SyntaxErrorCause[SyntaxErrorCause.FAILED_PREDICATE], "{msg}"); //this one should not be used. It indicates an unexpected error on our side
  }

  // Strings related to semantic errors
  private defineSemanticErrorStrings() {
    this.define(SemanticErrorCause[SemanticErrorCause.DOUBLE_DECLARATION_PROGRAM_NAME], "Ce nom de programme a déjà été utilisé plus haut"); 
    this.define(SemanticErrorCause[SemanticErrorCause.DOUBLE_DECLARATION_PROGRAM_PARAMETER], "Ce nom de paramètre est utilisé deux fois: {parameter}");
    this.define(SemanticErrorCause[SemanticErrorCause.INVALID_PROGRAM_NAME], "Ce nom de programme n'est pas autorisé"); 
    this.define(SemanticErrorCause[SemanticErrorCause.NOT_ENOUGH_ARGUMENTS], "Pas assez de paramètres pour '{program}'. {number_of_arguments} paramètres attendus."); 
    this.define(SemanticErrorCause[SemanticErrorCause.TOO_MANY_ARGUMENTS], "Trop de paramètres pour '{program}'. {number_of_arguments} paramètres attendus."); 
    this.define(SemanticErrorCause[SemanticErrorCause.NO_SUCH_PROGRAM], "Le pogramme '{program}' n'existe pas ou contient une erreur.");
    this.define(SemanticErrorCause[SemanticErrorCause.NO_SUCH_VARIABLE], "La variable ou le paramètre '{variable}' n'a jamais été défini.");  
  }

  // Strings related to runtime errors
  private defineRuntimeErrorStrings() {
    this.define("runtime_error_unknown_program", "Le programme {program} n'existe pas ou contient une erreur.");
    this.define("runtime_error_unknown_function", "La fonction {function_name} n'existe pas. Il s'agit d'un bogue. Merci de signaler cette erreur.");
    this.define("runtime_error_unknown_builtin", "La commande '{command}' n'existe pas. Il s'agit d'un bogue. Merci de signaler cette erreur.");
    this.define("runtime_error_unknown_operator", "L'opérateur '{operator}' n'existe pas. Il s'agit d'un bogue. Merci de signaler cette erreur.");
    this.define("runtime_error_generic_invalid_argument", "Argument non valide pour '{program}': {argument}.");
    this.define("runtime_error_division_by_zero", "La division par 0 n'est pas possible.");
    this.define("runtime_error_generic_not_in_range", "Le paramètre pour {program} doit être compris entre {lower_bound} et {upper_bound}.");
    this.define("runtime_error_invalid_argument_tan", "Le paramètre pour tan ne doit pas avoir la forme (0.5 + k) * PI");
    this.define("runtime_error_mod_by_zero", "Calculer 'mod n 0' n'est pas possible");
    this.define("runtime_error_invalid_argument_colornumber", "Le numéro de couleur spécifié n'existe pas: {color_number}");
    this.define("runtime_error_invalid_argument_colorname", "Le nom de couleur spécifié n'existe pas: {color_name}");
    this.define("runtime_error_invalid_argument_rgb", "Les valeurs d'une couleur doivent être des nombre: {color}.");
    this.define("runtime_error_rgb_out_of_range", "La valeur de couleur spécifiée n'est pas valide: {color}");
    this.define("runtime_error_sqrt_negative_number", "Il n'est pas possible de calculer la racine carrée de nombres négatifs.");
    this.define("runtime_error_log10_negative_number", "Il n'est pas possible de calculer le logarithme de nombres négatifs.");
    this.define("runtime_error_setpw_negative_number", "Il n'est pas possible d'avoir un stylo d'une largeur négative.");
    this.define("runtime_error_wait_negative_number", "Il n'est pas possible d'attendre un temps négatif.");
    this.define("runtime_error_circle_negative_number", "Il n'est pas possible de dessiner un cercle avec un rayon négatif.");
  }

  // Strings related to quickstarter
  private defineQickStartStrings() {
    this.define("tutorialtype", "Choisis un tutoriel.");
    this.define("doyouneedhelp", "As-tu besoin d'un coup de main?");
    this.define("firststeps", "Premiers pas");
    this.define("definenewcommands", "Définis de nouvelles commandes");
    this.define("debugger", "Exécution pas à pas");
    this.define("intro1", "Bienvenue sur XLogoOnline. Dans ce tutoriel, tu apprenderas comment programmer des motifs colorés.");
    this.define("intro2", "Regarde, la tortue dessine un mandala! Ce que nous voyons est le résultat d'un programme.");
    this.define("intro3", "Les programmes sont constitués de commandes. Deux de ces commandes sont FD et BK (que nous utilisons pour tracer des lignes). RT et LT tournent la tortue vers la droite et la gauche.");
    this.define("intro4", "Essaye-le! Clique dans le champ et confirme la commande avec ENTER.");
    this.define("intro5", "La tortue exécutera tous les ordres que tu lui donneras. Tu peux voir le résultat ici sur la planche à dessin");
    this.define("intro6", "Supprime le dessin avec la commande CS.");
    this.define("intro7", "CS efface toutes les lignes et remet la tortue au centre.");
    this.define("intro8", "Les programmes peuvent également se composer de plusieurs commandes. Sais-tu que dessine ce programme?");
    this.define("intro9", "Tous les programmes qui ont déjà été exécutés sont listés ici l'un après l'autre.");
    this.define("intro10", "Avec les TOUCHES FLÉCHÉES " + String.fromCharCode(8679) + " et " + String.fromCharCode(8681) + " les programmes précédents peuvent être chargés à nouveau dans la ligne de saisie sans les réécrire.");
    this.define("intro11", "Au fait, que se passe-t-il quand on fait une erreur?");
    this.define("intro12", "Ce programme est inorrect. Vois-tu l'erreur? Teste ce que la tortue fait face aux erreurs.");
    this.define("intro13", "La tortue n'a pas compris l'ordre. Au lieu d'un dessin, nous obtenons un indice sur l'erreur et sa cause (ici en orange).");
    this.define("intro14", "Allons-y. Bonne programmation!");
    this.define("editor1", "Dans ce tutoriel, nous te montrerons comment définir et utiliser tes propres commandes.");
    this.define("editor2", "Regarde ce programme complexe! Qui devine ce que fait le programme?");
    this.define("editor3", "Quoi? Un programme si compliqué, juste pour dessiner quatre carrés?!");
    this.define("editor4", "N'y a-t-il pas une solution plus simple, par exemple comme celle-ci?");
    this.define("editor5", "Malheureusement non, la tortue ne connaît pas la commande QUADRAT.... Alors définissons-la!");
    this.define("editor6", "De nouvelles commandes sont définies ici. Cette zone est appelée éditeur.");
    this.define("editor7", "'TO' QUADRAT 'END' définit une nouvelle commande appelée QUADRAT.");
    this.define("editor8", "La même fenêtre d'éditeur peut être utilisée pour définir plusieurs commandes.");
    this.define("editor9", "Oups! Vois-tu la croix rouge sur la ligne 5? Elle indique qu'une erreur est cachée sur cette ligne. Que pourrait-ce être? La même commande QUADRAT a été définie plusieures fois. (Astuce: Après ce tutoriel, tu pourras passer la souris sur la croix et en savoir plus sur l'erreur)");
    this.define("editor10", "Que doit-il se passer exactement lorsque la commande QUADRAT est appelée? Nous écrivons les commandes correspondantes entre TO et END.");
    this.define("editor11", "Testons ce que le nouvel ordre va faire. Qu'attends tu? Essaye-le.");
    this.define("editor12", "Un carré, et avec une seule commande! Pratique! Maintenant, des images encore plus compliquées peuvent être facilement dessinées avec cette commande....");
    this.define("editor13", "Félicitations. Tu as défini ta première commande personnelle (dans l'éditeur) et l'as appelée. Bonne programmation!");
  }

  // Strings related to storage functionality
  private defineOnlineStorageStrings() {
    this.define("loginButton", "Connexion");
    this.define("logoutButton", "Se déconnecter");
    this.define("registerButton", "Créer un compte");
    this.define("loginTitle", "Connexion");
    this.define("username", "Nom d'utilisateur");
    this.define("password", "Mot de passe (facultatif)");
    this.define("successfulLoginTitle", "Connecté avec succès");
    this.define("successfulLoginText", "Tu es maintenant connecté(e) en tant que ");
    this.define("failLoginTitle", "Échec de la connexion");
    this.define("tryAgain", "Retenter l'opération!");
    this.define("registerTitle", "Enregistrement d'un nouvel utilisateur");
    this.define("repeatPassword", "Confirmer le mot de passe");
    this.define("passwordMisatchTitle", "Mots de passe différents");
    this.define("passwordMisatchText", "Les deux mots de passe fournis ne concordent pas. Veux-tu réessayer?");
    this.define("successfulRegistrationTitle", "Compte créé avec succès");
    this.define("successfulRegistrationText", "Félicitations! Tu as créé ton compte avec succès et tu es maintenant connecté(e) en tant que ");
    this.define("serversuccess", "Succès");
    this.define("unknownuser", "Utilisateur inconnu sur le serveur");
    this.define("wrongpassword", "Mot de passe incorrect");
    this.define("usernamealreadytaken", "Ce nom d'utilisateur est déjà utilisé. Choisis-en un autre!");
    this.define("Ihaveread", "Je confirme avoir lu et accepté la ");
    this.define("datenschutz", "politique de confidentialité");
    this.define("aswellas", "et les");
    this.define("terms", "modalités et conditions");
    this.define("readandaccepted", "et je l'approuve.");
    this.define("toomuchtext", "Ce document est trop long");
    this.define("toomuchtexthint", "Conseil: Pourquoi ne pas décomposer ta solution en plusieurs parties?");
    this.define("documentexists", "Ce document existe");
    this.define("overwrite", "Veux-tu que je le cède?");
    this.define("writtensuccessfully", "Sauvegardé avec succès");
    this.define("doYouWantToLogOut", "Prêt à te déconnecter?");
    this.define("didYouSaveYourPrograms", "Est-ce que tu as sauvegardé tes programmes?");
    this.define("announceTitle", "Cherche puzzles");
  }

  // Strings related to Exam mode
  private defineExamStrings(){
    this.define("handInTask", "Envoyer la tâche");
  this.define("choseAnOption", "Sélectionne une des options suivantes");
  this.define("startExamMode", "Tâches de puzzle");
  this.define("waitingForExamToStart", "Prêt. En attente du départ");
  this.define("examFinished","Le concours est terminé.");
  this.define("checkingForExam", "Charger le concours...");
  this.define("cancelExamModeButton", "Annuler");
  this.define("enterExamModeButton", "Charger le concours...");
  this.define("infoRecognition", "Mot d'identification du concours.");
  this.define("notauthorizedTitle", "Enregistrement obligatoire");
  this.define("notauthorizedText","Pour participer au concours, il est nécessaire de se connecter.");
  }

  // Strings related to login
  private defineLoginStrings() {
    this.define("notingtosave", "Rien à sauvegarder");
  this.define("howtosave", "Comment effectuer la sauvegarde?");
  this.define("save", "Sauvegarder");
  this.define("locally", "sur l'ordinateur");
  this.define("online", "en ligne");
  this.define("load", "Charger des programmes");
  this.define("noconnection", "Échec de la connexion");
  this.define("filename", "Nom de mon dossier: ");
  this.define("login", "Login: ");
  this.define("chapterx", "Chapitre X Tâche Y");
  this.define("loadprogram", "charge un programme:");
  this.define("chooseprogram", "choisis un programme");
  this.define("youcanloadandstore", "To peux maintenant sauvegarder et recharger des programmes en ligne");
  this.define("logoutsuccess", "Tu es déconnecté(e)");
  }

  // These strings are static and do not need to be translated ever!
  populateStaticStrings() {
    this.define("XLogoOnline", "XLogoOnline");
  }
}



// WEBPACK FOOTER //
// ./src/app/languages/french.service.ts